import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Input, Spin, Row, Col, DatePicker } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import CustomModal from '../../../components/common/CustomModal';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import * as constant from '../../../actions/constant';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getSport,
  getSeason,
  addSeason,
  removeSeason,
  API_URL,
} from '../../../store/slices/adminSlice';
import { jsonApiHeader } from '../../../actions/utilAction';
import { useTableState, useModal, useDeleteConfirm, useDataLoading } from '../../../hooks';
import { requiredRule } from '../../../utils';
import toastr from 'toastr';
import axios from 'axios';
import { Moment } from 'moment';

const { confirm } = Modal;
const { RangePicker } = DatePicker;

// TypeScript interfaces
interface SportData {
  _id: string;
  sportname: string;
  description?: string;
}

interface SeasonData {
  _id: string;
  seasonname: string;
  startDate: string;
  endDate: string;
  isActive: boolean | null;
  sport?: string[];
}

interface SeasonFormValues {
  seasonname: string;
  date: [Moment, Moment];
}

const Season: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<SeasonFormValues>();

  // Redux state
  const sportData = useAppSelector((state) => state.admin.sportData) as unknown as SportData[];
  const seasonData = useAppSelector((state) => state.admin.seasonData) as SeasonData[];

  // Use custom hooks for common patterns
  const { sortOrder, sortData } = useTableState({
    fetchAction: getSeason,
  });

  const { visible, showModal, hideModal } = useModal({ form });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeSeason,
    idParamName: 'seasonId',
  });

  const [loading] = useDataLoading(seasonData);

  // Local state for selected sports
  const [selectedSport, setSelectedSport] = useState<string[]>([]);

  // Load sport data on mount
  useEffect(() => {
    dispatch(getSport({ count: 1000, page: 0 }));
  }, [dispatch]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const obj = {
        seasonname: values.seasonname,
        startDate: values.date[0].format(),
        endDate: values.date[1].format(),
        sport: selectedSport,
      };

      if (selectedSport.length === 0) {
        toastr.error('please Select a Sports');
        return;
      }

      confirm({
        title: `Adding Season you can't editable so please confirm`,
        onOk() {
          dispatch(addSeason(obj));
          hideModal();
          form.resetFields();
          setSelectedSport([]);
        },
        onCancel() {},
      });
    } catch (error) {
      confirm({
        title: 'Please enter details',
        onOk() {},
        onCancel() {},
      });
    }
  };

  const handleCancel = () => {
    hideModal();
    setSelectedSport([]);
  };

  const showSessionConfirm = () => {
    const access_token = localStorage.access_token;

    axios
      .get(API_URL + constant.Check, {
        headers: jsonApiHeader(access_token, 'application/json'),
      })
      .then(function (response) {
        if (response.data.code === 200) {
          toastr.success(response.data.message);
        } else if (
          response.data.code === 201 ||
          response.data.code === 400 ||
          response.data.code === 409
        ) {
          toastr.error(response.data.message);
        }
      });

    const updatedSports: string[] = [];
    sportData.forEach((x) => {
      if (!selectedSport.includes(x._id)) {
        updatedSports.push(x._id);
      }
    });
    setSelectedSport([...selectedSport, ...updatedSports]);
    showModal('');
  };

  const onSelectSport = (e: React.MouseEvent<HTMLInputElement>, name: string) => {
    if (selectedSport.includes(name)) {
      const list = selectedSport.filter((x) => x !== name);
      setSelectedSport(list);
    } else {
      setSelectedSport([...selectedSport, name]);
    }
  };

  // Render season rows
  const seasonRows = seasonData.map((x) => (
    <tr key={x._id}>
      <td>{x.seasonname}</td>
      <td>{x.startDate}</td>
      <td>{x.endDate}</td>
      <td>{x.isActive === true ? 'True' : x.isActive === null ? '' : 'false'}</td>
      <td>{x.isActive === true ? 'In progress' : x.isActive === null ? '' : 'Completed'}</td>
      <td className="action-icons">
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => showDeleteConfirm(x._id)}
        >
          <i className="fa fa-trash" style={{ color: '#666' }}></i>
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="Season"
          subTitle=""
          extra={[
            <Button
              key="1"
              type="primary"
              onClick={showSessionConfirm}
              style={{
                float: 'right',
              }}
            >
              Start New Season
            </Button>,
          ]}
        />
        <Spin spinning={loading} delay={500}>
          <div className="box-body">
            <table className="table">
              <thead>
                <tr>
                  <SortableTableHeader
                    label="Season Name"
                    sortKey="seasonname"
                    currentSortOrder={sortOrder}
                    onSort={sortData}
                  />
                  <th scope="col">Start Date</th>
                  <th scope="col">End Date</th>
                  <th scope="col">Is Current</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>{seasonRows}</tbody>
            </table>
            <CustomModal
              handleCancel={handleCancel}
              handleOk={handleOk}
              visible={visible}
              title="Season"
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={form} className="sport-form l_hgt0" layout="vertical">
                <Form.Item
                  label="Season Name"
                  name="seasonname"
                  rules={[requiredRule('Season name')]}
                >
                  <Input placeholder="Enter Season Name" />
                </Form.Item>

                <Form.Item
                  label="Select start date and end date"
                  name="date"
                  rules={[{ required: true, message: 'Please enter date range' }]}
                >
                  <RangePicker placeholder={['Start Date', 'End Date']} />
                </Form.Item>

                <Row>
                  {sportData.map((x, index) => {
                    return (
                      <Col span={6} key={x._id}>
                        <label
                          className="control-label col-md-12 labelappt"
                          style={{ padding: '0px' }}
                        >
                          <input
                            type="checkbox"
                            className="appointmentfor"
                            name="AppointmentFor[]"
                            defaultValue={11}
                            checked={selectedSport.includes(x._id)}
                            onClick={(e) => onSelectSport(e as any, x._id)}
                          />
                          <b style={{ marginLeft: '10px' }}>{x.sportname}</b>
                        </label>
                      </Col>
                    );
                  })}
                </Row>
              </Form>
            </CustomModal>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default Season;
